































import {Component, Mixins, Prop} from 'vue-property-decorator';
import {ActiveInject} from "@/support/reactive"
import JdsIcon from "@/design-system/icons/JdsIcon.vue"
import JdsGlyph from "@/design-system/icons/JdsGlyph.vue"
import {Location} from "vue-router"
import AnalyticsEmitterMixin from "@/design-system/foundation/AnalyticsProvider/AnalyticsEmitterMixin"
import JdhInlineLoader from "@/design-system/shared/JdhInlineLoader.vue"

/**
 * A standard button for initiating actions or navigating around.
 * @status stable
 * @published 2.0.0
 * @changelog 2.0.0 Published.
 * @changelog 4.1.0 Add href prop.
 * @changelog 4.2.0 Add tight button type and stability improvements.
 * @changelog 4.3.0 Switch secondary to use purple, thicker borders on tertiary.
 * @changelog 4.4.1 Enforce cursor pointer on all buttons, reduce spacing for icons.
 * @changelog 4.5.5 Add click analytics event.
 * @changelog 5.4.0 Add loading state.
 *
 * @analytics jds.button:click Fired on user-initiated click.
 */
@Component({
    components: {JdhInlineLoader, JdsGlyph, JdsIcon}
})
export default class JdsButton extends Mixins(AnalyticsEmitterMixin) {
    /**
     * Reflects how important the button is for the expected user flow. There should only be one primary button
     * per UI entity. Secondary buttons are for non-standard flows. Tertiary buttons are for "maintenance doors".
     * @default "secondary"
     */
    @Prop({default: null}) type!: 'primary'|'secondary'|'tertiary'|'danger';

    /**
     * If this button **takes the user elsewhere in the app*, pass in a vue-router-compatible Location here.
     * The button will show an arrow to indicate that it will perform a page change, unless it's a micro button
     * or it already has an icon explicitly set.
     *
     * Mutually exclusive with `href`.
     */
    @Prop() to!: Location;

    /**
     * If this button **takes the user to another website**, off the app, pass in a URL here. Use the `to`
     * property when redirecting to another part of the same app.
     *
     * Mutually exclusive with `to`.
     */
    @Prop() href!: string;

    /**
     * Determines how tall this button renders.
     * @default "medium"
     * @avoid Usually pre-defined for you by other components and patterns.
     */
    @Prop({default: null}) size!: 'high'|'medium'|'low'|'tight';

    /**
     * @avoid Disabling a button is a last resort. Don't disable a button in place of validation or without an
     *  explanation on how to enable it again. Do **not** use this unless explicitly required.
     */
    @Prop({default: false, type: Boolean}) disabled!: boolean;

    /**
     * Show a compact and label-free button. Only to be used for advanced contexts.
     * @default false
     * @avoid
     */
    @Prop({default: undefined, type: Boolean}) micro!: boolean|undefined;

    /**
     * Hide the tertiary button's border. Only to be used when the button is in an obvious bounding
     * context, such as a table cell.
     * @default false
     * @avoid
     */
    @Prop({default: undefined, type: Boolean}) borderless!: boolean|undefined;

    /**
     * **Only** use an icon when one exists for the action being invoked. Don't use an icon for the sake of using
     * an icon -- aim to be consistent with which actions have icons.
     */
    @Prop() icon!: string;

    /**
     * Show a loader instead of the button's regular contents, and disables click events. Use
     * **only** in response to a user action, such as a click, and be sure to reset the button
     * afterwards. Gracefully handle errors, too!
     *
     * Do **not** use on micro buttons.
     */
    @Prop({default: false, type: Boolean}) loading!: boolean

    @ActiveInject({from: 'jds.button.size'}) injectedSize!: string;
    @ActiveInject({from: 'jds.button.type'}) injectedType!: string;
    @ActiveInject({from: 'jds.button.micro'}) injectedMicro!: boolean|undefined;
    @ActiveInject({from: 'jds.button.borderless'}) injectedBorderless!: boolean|undefined;

    get classes () {
        return [
            "jdsc-button-" + this.effectiveType,
            "jdsc-button-" + this.effectiveSize,
            ... (this.effectiveMicro ? ['jdsc-button-micro'] : []),
            ... (this.effectiveBorderless ? ['jdsc-button-borderless'] : []),
            ... (this.disabled ? ['jdsc-button-disabled'] : [])
        ]
    }

    get tag () {
        if (this.to) return 'router-link'
        if (this.href) return 'a'
        return 'button'
    }

    get showArrow () {
        if (this.icon || this.effectiveMicro) return false
        return this.to || this.href;
    }

    get effectiveSize () {
        return this.size || this.injectedSize || 'medium';
    }

    get effectiveType () {
        return this.type || this.injectedType || 'secondary';
    }

    get effectiveMicro () {
        if (this.micro != undefined) return this.micro;
        if (this.injectedMicro != undefined) return this.injectedMicro;
        return false;
    }

    get effectiveBorderless () {
        if (this.borderless != undefined) return this.borderless;
        if (this.injectedBorderless != undefined) return this.injectedBorderless;
        return false;
    }

    emitClick (event: MouseEvent) {
        if (!this.disabled && !this.loading) {
            /**
             * Emitted on every user-initiated click.
             * @param {MouseEvent} event
             */
            this.$emit('click', event);
            this.$analytics('click')
        }
    }
}
